import { ShieldCheckIcon } from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import { Button } from 'components/atoms/Button';
import ModalHeading from 'components/molecules/ModalHeading/ModalHeading.component';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { WEBAPP_BANKING_ONBOARDING_OURPROMISES_CLICKNEXT } from 'utils/amplitude/events/bank.amplitude';
import { fadeTransition } from 'utils/constants';

const OurPromises: FC<{ onConfirm?: () => void }> = ({ onConfirm }) => {
  const { t } = useTranslation();

  const user = useGetCurrentCustomerData().data;

  const accountableBankingFeatures = [
    {
      title: t(
        'accountable_banking.onboarding.our_promises.total_transparency.subtitle',
      ),
      subtitle: t(
        'accountable_banking.onboarding.our_promises.total_transparency.title',
      ),
    },
    {
      title: t(
        `accountable_banking.onboarding.our_promises.iban.title.${user?.country}`,
      ),
      subtitle: t(
        `accountable_banking.onboarding.our_promises.iban.subtitle.${user?.country}`,
      ),
    },
    {
      title: t(
        'accountable_banking.onboarding.our_promises.instant_access.title',
      ),
      subtitle: t(
        'accountable_banking.onboarding.our_promises.instant_access.subtitle',
      ),
    },
    {
      title: t(
        'accountable_banking.onboarding.our_promises.deposit_protection.title',
      ),
      subtitle: t(
        'accountable_banking.onboarding.our_promises.deposit_protection.subtitle',
      ),
    },
  ];

  return (
    <motion.div
      {...fadeTransition}
      className="flex items-center justify-center flex-col relative pb-20"
      key="5"
    >
      <ModalHeading
        Icon={ShieldCheckIcon}
        title={t('accountable_banking.onboarding.our_promises.title')}
        subtitleClassName="w-[80%]"
      />

      <div className="flex-1 w-[80%] mx-auto my-6">
        {accountableBankingFeatures.map((feature, i) => (
          <motion.div
            initial={{ opacity: 0, translateY: 50 }}
            animate={{
              opacity: 1,
              translateY: 0,
              transition: {
                delay: i === 0 ? 0.25 : (i * 6) / 15,
              },
            }}
            className="flex items-center space-x-4 my-3"
            key={feature.title}
          >
            <div className="w-10 h-10 rounded-full bg-bank-100 p-2">
              <CheckIcon className="text-bank" />
            </div>

            <div className="text-left">
              <h3 className="font-bold text-primary-800">{feature.title}</h3>

              <p className="text-primary-700">{feature.subtitle}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <Button
        color="bank"
        className="mt-6 w-[80%] absolute bottom-0 capitalize"
        tracingEvent={WEBAPP_BANKING_ONBOARDING_OURPROMISES_CLICKNEXT}
        onClick={onConfirm}
      >
        {t('next')}
      </Button>
    </motion.div>
  );
};

export default OurPromises;
